import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import FileUploadIcon from "@mui/icons-material/FileUpload"
import DescriptionIcon from "@mui/icons-material/Description"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ArchiveIcon from "@mui/icons-material/Archive"
import UnarchiveIcon from "@mui/icons-material/Unarchive"
import AddIcon from "@mui/icons-material/Add"
import SearchIcon from "@mui/icons-material/Search"
import InventoryIcon from "@mui/icons-material/Inventory"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import { getStockAlertsSummary } from "../../services/itemsService"
import { getOutletVatRates } from "../../services/outletSettingsService";
import { t } from "i18next"

const thStyle = {
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "uppercase" as const,
  color: "#6b7280",
  letterSpacing: "0.05em",
}

function toFieldLabel(field: string, t: any) {
  const key = String(field || "")
    .trim()
    .toLowerCase()
  const labels: Record<string, string> = {
    sku: String(t("itemsAdminPage.fields.sku")),
    name: String(t("itemsAdminPage.fields.name")),
    vat_rate_id: String(t("itemsAdminPage.fields.vatRate")),
    outlet_id: String(t("itemsAdminPage.fields.outlet")),
    category_id: String(t("itemsAdminPage.fields.category")),
    unit_price_minor: String(t("itemsAdminPage.fields.salePrice")),
    purchase_price_minor: String(t("itemsAdminPage.fields.purchasePrice")),
    barcode: String(t("itemsAdminPage.fields.barcode")),
    qty: String(t("itemsAdminPage.fields.quantity")),
  }
  return labels[key] || field;
}
function computeSalePriceTtcMinor(
  unitPriceMinor: number | undefined | null,
  vatRatePercent: number | undefined | null,
): number {
  const normalizedUnitPriceMinor = Number(unitPriceMinor || 0);
  if (!Number.isFinite(normalizedUnitPriceMinor) || normalizedUnitPriceMinor <= 0) {
    return 0;
  }

  const normalizedVatRatePercent = Number(vatRatePercent || 0);
  return Math.round(normalizedUnitPriceMinor * (1 + normalizedVatRatePercent / 100));
}

type VatRateOption = {
  id: string;
  label: string;
  percentage: number;
  is_enabled: boolean;
  is_active: boolean;
};

function extractItemFormErrors(error: any, t: any): string[] {
  const messages: string[] = []
  const details = error?.details

  if (Array.isArray(details)) {
    details.forEach((detail: any) => {
      const msg = String(detail?.message || detail || "").trim()
      if (msg) messages.push(msg)
    })
  } else if (details && typeof details === "object") {
    Object.entries(details).forEach(([field, detail]) => {
      const msg = String((detail as any)?.message || detail || "").trim()
      if (msg) {
        messages.push(`${toFieldLabel(field, t)}: ${msg}`)
      }
    })
  }

  const rawMessage = String(error?.message || error?.userMessage || "").trim()
  const missingPrefix = "Missing required fields:"
  if (rawMessage.includes(missingPrefix)) {
    const missingFieldsRaw = rawMessage
      .split(missingPrefix)[1]
      ?.split(",")
      .map((f) => f.trim())
      .filter(Boolean)

    if (missingFieldsRaw?.length) {
      return missingFieldsRaw.map((field) =>
        String(
          t("itemsAdminPage.validation.requiredField", {
            field: toFieldLabel(field, t),
          })
        )
      )
    }
  }

  if (messages.length > 0) {
    return messages.filter((message, index, array) => {
      return array.indexOf(message) === index
    })
  }

  if (rawMessage) {
    return [rawMessage]
  }

  return [String(t("itemsAdminPage.errors.saveFailed"))]
}

const ItemsAdminPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const formatMinor = (minor: number | undefined | null) =>
    new Intl.NumberFormat(i18n.language || "fr", {
      style: "currency",
      currency: "EUR",
    }).format(Number(minor || 0) / 100)
  const [companyId, setCompanyId] = useState("")
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [includeArchived, setIncludeArchived] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [stockAlertsCount, setStockAlertsCount] = useState(0)
  const [saveErrorMessages, setSaveErrorMessages] = useState<string[]>([])
  const [saveErrorOpen, setSaveErrorOpen] = useState(false)
  const [vatRateOptions, setVatRateOptions] = useState<VatRateOption[]>([]);
  const [outlets, setOutlets] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [filterOutletId, setFilterOutletId] = useState("")
  const [filterCategoryId, setFilterCategoryId] = useState("")
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null
  )
  const importInputRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)
  const [importResultOpen, setImportResultOpen] = useState(false)
  const [importResult, setImportResult] = useState<{
    severity: "success" | "warning" | "error"
    title: string
    messages: string[]
  } | null>(null)

  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    description: "",
    category_id: "",
    vat_rate_id: "",
    barcode: "",
    outlet_id: "",
    qty: 0,
    purchase_price_minor: 0,
    unit_price_minor: 0,
  })
  const selectedVatRatePercent = useMemo(() => {
    const selected = vatRateOptions.find((rate) => rate.id === formData.vat_rate_id);
    return Number(selected?.percentage || 0);
  }, [vatRateOptions, formData.vat_rate_id]);

  const salePriceTtcMinor = useMemo(
    () => computeSalePriceTtcMinor(formData.unit_price_minor, selectedVatRatePercent),
    [formData.unit_price_minor, selectedVatRatePercent],
  );
  useEffect(() => {
    let cancelled = false
      ; (async () => {
        try {
          const res = await apiClient.get("/api/companies")
          if (cancelled) return
          const list = res.companies || []
          if (list.length > 0) setCompanyId(list[0].id)
        } catch (e: any) {
          console.error("Failed to load companies:", e)
        }
      })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!companyId) return
    let cancelled = false
      ; (async () => {
        try {
          const [outletsRes, categoriesRes] = await Promise.all([
            apiClient.get(`/api/outlets?company_id=${companyId}`),
            apiClient.get(`/api/companies/${companyId}/categories`),
          ])
          if (cancelled) return
          setOutlets(outletsRes.outlets || [])
          setCategories(categoriesRes.categories || [])
        } catch (e: any) {
          console.error("Failed to load outlets/categories:", e)
        }
      })()
    return () => {
      cancelled = true
    }
  }, [companyId])
  useEffect(() => {
    if (!companyId) return;

    const effectiveOutletId = formData.outlet_id || outlets[0]?.id;
    if (!effectiveOutletId) {
      setVatRateOptions([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const rates = await getOutletVatRates(companyId, effectiveOutletId, {
          enabledOnly: true,
        });
        if (!cancelled) {
          setVatRateOptions(rates || []);
        }
      } catch {
        if (!cancelled) {
          setVatRateOptions([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, formData.outlet_id, outlets]);

  useEffect(() => {
    if (vatRateOptions.length === 0) return;

    const isCurrentValueAllowed = vatRateOptions.some(
      (rate) => rate.id === formData.vat_rate_id,
    );

    if (isCurrentValueAllowed) return;

    setFormData((prev) => ({ ...prev, vat_rate_id: vatRateOptions[0].id }));
  }, [formData.vat_rate_id, vatRateOptions]);
  const loadItems = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const [data, stockAlertsSummary] = await Promise.all([
        adminService.getItems(companyId, {
          search: searchQuery,
          includeArchived,
          page: page + 1,
          pageSize,
          outletId: filterOutletId || undefined,
          categoryId: filterCategoryId || undefined,
        }),
        getStockAlertsSummary(companyId),
      ])
      setItems(data.items || [])
      setTotal(data.total || 0)
      setStockAlertsCount(Number(stockAlertsSummary.count || 0))
    } catch (error) {
      console.error("Failed to load items:", error)
    } finally {
      setLoading(false)
    }
  }, [
    companyId,
    searchQuery,
    includeArchived,
    page,
    pageSize,
    filterOutletId,
    filterCategoryId,
  ])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleOpenDialog = (item?: any) => {
    setSaveErrorMessages([])
    setSaveErrorOpen(false)
    if (item) {
      setEditingItem(item)
      setFormData({
        sku: item.sku,
        name: item.name,
        description: item.description || "",
        category_id: item.category_id || "",
        vat_rate_id: item.vat_rate_id,
        barcode: item.barcode || "",
        outlet_id: item.outlet_id || "",
        qty: item.qty ?? 0,
        purchase_price_minor: item.purchase_price_minor ?? 0,
        unit_price_minor: item.unit_price_minor ?? 0,
      })
    } else {
      setEditingItem(null)
      setFormData({
        sku: "",
        name: "",
        description: "",
        category_id: "",
        vat_rate_id: "",
        barcode: "",
        outlet_id: "",
        qty: 0,
        purchase_price_minor: 0,
        unit_price_minor: 0,
      })
    }
    setOpenDialog(true)
  }

  const handleRowClick = (itemId: string) => {
    navigate(`/admin/items/${itemId}`)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingItem(null)
    setSaveErrorMessages([])
    setSaveErrorOpen(false)
  }

  const getClientValidationErrors = () => {
    const errors: string[] = []
    if (!editingItem && !String(formData.sku || "").trim()) {
      errors.push(
        String(
          t("itemsAdminPage.validation.requiredField", {
            field: t("itemsAdminPage.fields.sku"),
          })
        )
      )
    }
    if (!String(formData.name || "").trim()) {
      errors.push(
        String(
          t("itemsAdminPage.validation.requiredField", {
            field: t("itemsAdminPage.fields.name"),
          })
        )
      )
    }
    if (!String(formData.vat_rate_id || "").trim()) {
      errors.push(
        String(
          t("itemsAdminPage.validation.requiredField", {
            field: t("itemsAdminPage.fields.vatRate"),
          })
        )
      )
    }
    return errors
  }

  const handleSave = async () => {
    if (!companyId) return

    const clientErrors = getClientValidationErrors()
    if (clientErrors.length > 0) {
      setSaveErrorMessages(clientErrors)
      setSaveErrorOpen(true)
      return
    }

    try {
      if (editingItem) {
        await adminService.updateItem(
          companyId,
          editingItem.id,
          formData
        )
      } else {
        await adminService.createItem(companyId, formData)
      }
      setSaveErrorMessages([])
      setSaveErrorOpen(false)
      handleCloseDialog()
      loadItems()
    } catch (error: any) {
      const parsedErrors = extractItemFormErrors(error, t)
      setSaveErrorMessages(parsedErrors)
      setSaveErrorOpen(true)
      console.error("Failed to save item:", error)
    }
  }

  const handleArchive = async (itemId: string) => {
    if (!companyId) return
    try {
      await adminService.archiveItem(companyId, itemId)
      loadItems()
    } catch (error) {
      console.error("Failed to archive item:", error)
    }
  }

  const handleUnarchive = async (itemId: string) => {
    if (!companyId) return
    try {
      await adminService.unarchiveItem(companyId, itemId)
      loadItems()
    } catch (error) {
      console.error("Failed to unarchive item:", error)
    }
  }

  const exportFilterOptions = () => ({
    search: searchQuery || undefined,
    includeArchived: includeArchived || undefined,
    outletId: filterOutletId || undefined,
    categoryId: filterCategoryId || undefined,
  })

  const handleExport = async (format: "csv" | "pdf") => {
    setExportAnchorEl(null)
    if (!companyId) return
    try {
      const opts = exportFilterOptions()
      const response =
        format === "csv"
          ? await adminService.exportItemsCsv(companyId, opts)
          : await adminService.exportItemsPdf(companyId, opts)
      const url = window.URL.createObjectURL(response.blob)
      const a = document.createElement("a")
      a.href = url
      a.download = response.filename || `export.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error(`Failed to export ${format}:`, error)
    }
  }

  const handleImportClick = () => {
    if (!companyId || importing) return
    importInputRef.current?.click()
  }

  const handleImportFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || !companyId) return

    setImporting(true)
    try {
      const csvData = await file.text()
      const result = await adminService.importItemsCsv(companyId, csvData)

      const created = Number(result?.created || 0)
      const updated = Number(result?.updated || 0)
      const errors: Array<{ row: number; sku?: string; error: string }> =
        Array.isArray(result?.errors) ? result.errors : []
      const warnings: Array<{ row: number; sku?: string; warning: string }> =
        Array.isArray(result?.warnings) ? result.warnings : []

      const summary = String(
        t("itemsAdminPage.import.summary", { created, updated })
      )

      const detailMessages: string[] = []
      errors.slice(0, 10).forEach((err) => {
        detailMessages.push(
          `Ligne ${err.row}${err.sku ? ` (${err.sku})` : ""}: ${err.error}`
        )
      })
      if (errors.length > 10) {
        detailMessages.push(`… +${errors.length - 10}`)
      }
      warnings.slice(0, 5).forEach((w) => {
        detailMessages.push(
          `Ligne ${w.row}${w.sku ? ` (${w.sku})` : ""}: ${w.warning}`
        )
      })
      if (warnings.length > 5) {
        detailMessages.push(`… +${warnings.length - 5}`)
      }

      const severity: "success" | "warning" | "error" =
        errors.length > 0
          ? "error"
          : warnings.length > 0
            ? "warning"
            : "success"

      setImportResult({
        severity,
        title: summary,
        messages: detailMessages,
      })
      setImportResultOpen(true)
      await loadItems()
    } catch (error: any) {
      console.error("Failed to import CSV:", error)
      setImportResult({
        severity: "error",
        title: String(t("itemsAdminPage.import.failed")),
        messages: [error?.message || String(error)],
      })
      setImportResultOpen(true)
    } finally {
      setImporting(false)
    }
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <InventoryIcon sx={{ fontSize: 28, color: "#374151" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t("itemsAdminPage.title")}
          </Typography>
          <Chip
            label={t("itemsAdminPage.count", { count: total })}
            size="small"
            sx={{
              backgroundColor: "#f3f4f6",
              fontWeight: 500,
              fontSize: "0.8rem",
            }}
          />
          {stockAlertsCount > 0 ? (
            <Chip
              icon={<WarningAmberIcon />}
              label={t("itemsAdminPage.stockAlertsCount", {
                count: stockAlertsCount,
              })}
              size="small"
              color="error"
              variant="outlined"
            />
          ) : null}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: "#7f1d1d",
            "&:hover": { backgroundColor: "#991b1b" },
            borderRadius: "8px",
            fontWeight: 600,
            px: 3,
          }}
        >
          {t("itemsAdminPage.actions.addItem")}
        </Button>
      </Box>

      {/* Search & Filters */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          placeholder={String(t("itemsAdminPage.searchPlaceholder"))}
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: "#9ca3af", fontSize: 20 }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: "1 1 250px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>
            {String(t("itemsAdminPage.filters.outlet"))}
          </InputLabel>
          <Select
            value={filterOutletId}
            label={String(t("itemsAdminPage.filters.outlet"))}
            onChange={(e) => {
              setFilterOutletId(e.target.value)
              setPage(0)
            }}
            sx={{ borderRadius: "8px", backgroundColor: "#f9fafb" }}
          >
            <MenuItem value="">
              {t("itemsAdminPage.filters.all")}
            </MenuItem>
            {outlets.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>
            {String(t("itemsAdminPage.filters.category"))}
          </InputLabel>
          <Select
            value={filterCategoryId}
            label={String(t("itemsAdminPage.filters.category"))}
            onChange={(e) => {
              setFilterCategoryId(e.target.value)
              setPage(0)
            }}
            sx={{ borderRadius: "8px", backgroundColor: "#f9fafb" }}
          >
            <MenuItem value="">
              {t("itemsAdminPage.filters.all")}
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={(e) => setExportAnchorEl(e.currentTarget)}
          sx={{
            borderRadius: "8px",
            borderColor: "#d1d5db",
            color: "#374151",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          {t("itemsAdminPage.actions.export")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            importing ? (
              <CircularProgress size={16} />
            ) : (
              <FileUploadIcon />
            )
          }
          onClick={handleImportClick}
          disabled={importing || !companyId}
          sx={{
            borderRadius: "8px",
            borderColor: "#d1d5db",
            color: "#374151",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          {t("itemsAdminPage.actions.import")}
        </Button>
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={handleImportFileChange}
        />
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={() => setExportAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={() => handleExport("csv")}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleExport("pdf")}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeArchived}
              onChange={(e) => {
                setIncludeArchived(e.target.checked)
                setPage(0)
              }}
              size="small"
            />
          }
          label={
            <Typography
              sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}
            >
              {t("itemsAdminPage.filters.includeArchived")}
            </Typography>
          }
        />
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
              color: "#9ca3af",
            }}
          >
            <InventoryIcon
              sx={{ fontSize: 48, mb: 1, opacity: 0.5 }}
            />
            <Typography sx={{ fontWeight: 500 }}>
              {t("itemsAdminPage.empty.title")}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem" }}>
              {t("itemsAdminPage.empty.subtitle")}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                  <TableCell sx={thStyle}>
                    {t("itemsAdminPage.columns.sku")}
                  </TableCell>
                  <TableCell sx={thStyle}>
                    {t("itemsAdminPage.columns.name")}
                  </TableCell>
                  <TableCell sx={thStyle}>
                    {t("itemsAdminPage.columns.outlet")}
                  </TableCell>
                  <TableCell sx={thStyle}>
                    {t("itemsAdminPage.columns.category")}
                  </TableCell>
                  <TableCell sx={thStyle} align="right">
                    {t("itemsAdminPage.columns.quantity")}
                  </TableCell>
                  <TableCell sx={thStyle} align="right">
                    {t(
                      "itemsAdminPage.columns.purchasePrice"
                    )}
                  </TableCell>
                  <TableCell sx={thStyle} align="right">
                    {t("itemsAdminPage.columns.salePrice")}
                  </TableCell>
                  <TableCell sx={thStyle}>
                    {t("itemsAdminPage.columns.alert")}
                  </TableCell>
                  <TableCell sx={thStyle} align="right">
                    {t("itemsAdminPage.columns.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    onClick={() => handleRowClick(item.id)}
                    sx={{
                      cursor: "pointer",
                      "&:last-child td": {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "#374151",
                        }}
                      >
                        {item.sku}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: "#6b7280",
                        }}
                      >
                        {item.outlet_name ||
                          t("common.dash")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: "#6b7280",
                        }}
                      >
                        {item.category_name ||
                          t("common.dash")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={item.qty ?? 0}
                        size="small"
                        sx={{
                          backgroundColor:
                            item.is_stock_alert
                              ? "#fef2f2"
                              : (item.qty ?? 0) <=
                                0
                                ? "#fff7ed"
                                : "#f3f4f6",
                          color: item.is_stock_alert
                            ? "#991b1b"
                            : (item.qty ?? 0) <= 0
                              ? "#9a3412"
                              : "#374151",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          minWidth: 40,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color: "#374151",
                        }}
                      >
                        {formatMinor(
                          item.purchase_price_minor
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color: "#374151",
                        }}
                      >
                        {formatMinor(
                          item.unit_price_minor
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.stock_alert_threshold ==
                        null ? (
                        <Chip
                          label={t(
                            "itemsAdminPage.alert.notDefined"
                          )}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: "#6b7280",
                            borderColor: "#d1d5db",
                          }}
                        />
                      ) : item.is_stock_alert ? (
                        <Chip
                          icon={<WarningAmberIcon />}
                          label={t(
                            "itemsAdminPage.alert.lowStockWithThreshold",
                            {
                              threshold:
                                item.stock_alert_threshold,
                            }
                          )}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      ) : item.stock_alert_threshold ===
                        0 ? (
                        <Chip
                          label={t(
                            "itemsAdminPage.alert.thresholdZero"
                          )}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: "#166534",
                            borderColor: "#86efac",
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "#6b7280",
                          }}
                        >
                          {t(
                            "itemsAdminPage.alert.thresholdLabel",
                            {
                              threshold:
                                item.stock_alert_threshold,
                            }
                          )}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleRowClick(item.id)
                        }}
                        sx={{ color: "#6b7280" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {item.is_archived ? (
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleUnarchive(item.id)
                          }}
                          sx={{ color: "#065f46" }}
                        >
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleArchive(item.id)
                          }}
                          sx={{ color: "#6b7280" }}
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && items.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage={t("common.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              String(
                t("common.paginationDisplayedRows", {
                  from,
                  to,
                  count,
                })
              )
            }
            sx={{
              borderTop: "1px solid #e5e7eb",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                fontSize: "0.85rem",
                color: "#6b7280",
              },
            }}
          />
        )}
      </Paper>

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingItem
            ? t("itemsAdminPage.dialog.editTitle")
            : t("itemsAdminPage.dialog.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              label={String(t("itemsAdminPage.fields.sku"))}
              required
              fullWidth
              size="small"
              value={formData.sku}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sku: e.target.value,
                })
              }
              disabled={!!editingItem}
            />
            <TextField
              label={String(t("itemsAdminPage.fields.name"))}
              required
              fullWidth
              size="small"
              value={formData.name}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  name: e.target.value,
                })
              }
            />
            <TextField
              label={String(
                t("itemsAdminPage.fields.description")
              )}
              fullWidth
              size="small"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  description: e.target.value,
                })
              }
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t("itemsAdminPage.fields.vatRateId")}</InputLabel>
                <Select
                  value={formData.vat_rate_id}
                  onChange={(e) =>
                    setFormData({ ...formData, vat_rate_id: String(e.target.value || "") })
                  }
                >
                  {vatRateOptions.map((rate) => (
                    <MenuItem key={rate.id} value={rate.id}>
                      {rate.label} ({Number(rate.percentage || 0).toFixed(2)}%)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={String(
                  t("itemsAdminPage.fields.barcode")
                )}
                fullWidth
                size="small"
                value={formData.barcode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    barcode: e.target.value,
                  })
                }
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {String(t("itemsAdminPage.fields.outlet"))}
                </InputLabel>
                <Select
                  value={formData.outlet_id}
                  label={String(
                    t("itemsAdminPage.fields.outlet")
                  )}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      outlet_id: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">
                    {t("itemsAdminPage.fields.none")}
                  </MenuItem>
                  {outlets.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>
                  {String(
                    t("itemsAdminPage.fields.category")
                  )}
                </InputLabel>
                <Select
                  value={formData.category_id}
                  label={String(
                    t("itemsAdminPage.fields.category")
                  )}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category_id: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">
                    {t("itemsAdminPage.fields.none")}
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label={String(
                  t("itemsAdminPage.fields.stockQuantity")
                )}
                type="number"
                fullWidth
                size="small"
                value={formData.qty}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    qty: parseInt(e.target.value, 10) || 0,
                  })
                }
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label={String(
                  t("itemsAdminPage.fields.salePriceMinor")
                )}
                type="number"
                sx={{ flex: "1 1 180px" }}
                size="small"
                value={formData.unit_price_minor}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    unit_price_minor:
                      parseInt(e.target.value, 10) || 0,
                  })
                }
                helperText={`= ${formatMinor(
                  formData.unit_price_minor
                )}`}
              />
              <TextField
                label={String(
                  t("itemsAdminPage.fields.salePriceTtcMinor")
                )}
                sx={{ flex: "1 1 180px" }}
                size="small"
                value={formatMinor(salePriceTtcMinor)}
                disabled
                InputProps={{ readOnly: true }}
              />
              <TextField
                label={String(
                  t(
                    "itemsAdminPage.fields.purchasePriceMinor"
                  )
                )}
                type="number"
                sx={{ flex: "1 1 180px" }}
                size="small"
                value={formData.purchase_price_minor}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    purchase_price_minor:
                      parseInt(e.target.value, 10) || 0,
                  })
                }
                helperText={`= ${formatMinor(
                  formData.purchase_price_minor
                )}`}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: "#6b7280" }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "#7f1d1d",
              "&:hover": { backgroundColor: "#991b1b" },
              borderRadius: "8px",
              fontWeight: 600,
            }}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveErrorOpen}
        autoHideDuration={6000}
        onClose={() => setSaveErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSaveErrorOpen(false)}
          sx={{ width: "100%", alignItems: "flex-start" }}
        >
          <Box
            component="div"
            sx={{
              fontWeight: 700,
              mb: saveErrorMessages.length > 1 ? 0.5 : 0,
            }}
          >
            {t("itemsAdminPage.errors.addFailedTitle")}
          </Box>
          {saveErrorMessages.length > 1 ? (
            <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
              {saveErrorMessages.map((msg, idx) => (
                <li key={`${idx}-${msg}`}>{msg}</li>
              ))}
            </Box>
          ) : (
            <Box component="div">
              {saveErrorMessages[0] ||
                t("itemsAdminPage.errors.saveFailed")}
            </Box>
          )}
        </Alert>
      </Snackbar>

      <Snackbar
        open={importResultOpen}
        autoHideDuration={importResult?.severity === "success" ? 5000 : 10000}
        onClose={() => setImportResultOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={importResult?.severity || "success"}
          variant="filled"
          onClose={() => setImportResultOpen(false)}
          sx={{ width: "100%", alignItems: "flex-start", maxWidth: 600 }}
        >
          <Box
            component="div"
            sx={{
              fontWeight: 700,
              mb: importResult?.messages?.length ? 0.5 : 0,
            }}
          >
            {importResult?.title || ""}
          </Box>
          {importResult?.messages?.length ? (
            <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
              {importResult.messages.map((msg, idx) => (
                <li key={`${idx}-${msg}`}>{msg}</li>
              ))}
            </Box>
          ) : null}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ItemsAdminPage
